import { captureAllClickIds, buildMetaEntries as buildMetaEntriesFromCapture } from '@sparefoot/tracking-client';
import { API } from '../middleware/api';

export const USER = {
	SAVE_BATCH_META_REQUEST: 'SFDC/USER/SAVE_BATCH_META_REQUEST',
	SAVE_BATCH_META_SUCCESS: 'SFDC/USER/SAVE_BATCH_META_SUCCESS',
	SAVE_BATCH_META_FAILURE: 'SFDC/USER/SAVE_BATCH_META_FAILURE',
	UNSUBSCRIBE_REQUEST: 'SFDC/USER/UNSUBSCRIBE_REQUEST',
	UNSUBSCRIBE_SUCCESS: 'SFDC/USER/UNSUBSCRIBE_SUCCESS',
	UNSUBSCRIBE_FAILURE: 'SFDC/USER/UNSUBSCRIBE_FAILURE'
};

export function unsubscribe(data) {
	return {
		[API]: {
			method: 'POST',
			types: [USER.UNSUBSCRIBE_REQUEST, USER.UNSUBSCRIBE_SUCCESS, USER.UNSUBSCRIBE_FAILURE],
			endpoint: '/unsubscribe',
			data
		}
	};
}

/**
 * Save multiple meta entries in a single request
 * @param {Array<{key: string, value: string}>} entries - Array of meta entries
 * @param {Object} meta - Redux action meta (gcid/gclid values for state update)
 */
export function saveBatchMeta(entries, meta = {}) {
	return {
		[API]: {
			method: 'POST',
			types: [USER.SAVE_BATCH_META_REQUEST, USER.SAVE_BATCH_META_SUCCESS, USER.SAVE_BATCH_META_FAILURE],
			endpoint: '/visit/meta',
			meta,
			data: {
				meta: entries
			}
		}
	};
}

const MAX_RETRIES = 10;
const RETRY_DELAY_MS = 300;
const STORAGE_KEY = 'sf_tracking_meta_sent';

/**
 * Get sent keys from sessionStorage for the current visitId.
 * Returns empty Set if visitId changed or no data exists.
 */
function getSentKeysForCurrentVisit(visitId) {
	if (typeof sessionStorage === 'undefined') {
		return new Set();
	}

	try {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (!stored) return new Set();

		const { vid, keys } = JSON.parse(stored);
		if (vid !== visitId) return new Set();

		return new Set(keys);
	} catch {
		return new Set();
	}
}

/**
 * Mark entries as sent for this visit only after a confirmed SAVE_BATCH_META_SUCCESS.
 * Persists to sessionStorage so state survives page reloads within the session.
 * On failure, keys are intentionally left unmarked so a retry re-includes them.
 */
function markEntriesAsSent(visitId, entries) {
	if (typeof sessionStorage === 'undefined') return;

	try {
		const existingKeys = getSentKeysForCurrentVisit(visitId);
		entries.forEach(({ key }) => existingKeys.add(key));

		sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
			vid: visitId,
			keys: Array.from(existingKeys)
		}));
	} catch {
		// sessionStorage may be full or disabled - fail silently
	}
}

// Keys currently in an in-flight saveBatchMeta request from either capture
// path. Separate from the sessionStorage "sent" record so a key that is
// mid-request isn't offered to the other path and posted twice.
let pendingKeys = new Set();

function markKeysPending(entries) {
	entries.forEach(({ key }) => pendingKeys.add(key));
}

function unmarkKeysPending(entries) {
	entries.forEach(({ key }) => pendingKeys.delete(key));
}

function logBatchMetaFailure(result) {
	if (result?.type === USER.SAVE_BATCH_META_FAILURE) {
		// eslint-disable-next-line no-console
		console.error('Failed to save tracking meta to tracking service', result.error);
	}
}

/**
 * Build array of meta entries that still need to be sent for this visit.
 * @param {Object} user - Current user state
 * @returns {{ entries: Array, meta: Object, gcidValue: string|null }}
 */
function buildMetaEntries(user) {
	const captured = captureAllClickIds();
	const alreadySent = getSentKeysForCurrentVisit(user.visitId);
	const excluded = new Set([...alreadySent, ...pendingKeys]);
	const entries = buildMetaEntriesFromCapture(captured, excluded);

	// meta only needs to carry gcid/gclid - the two keys the user slice
	// (SAVE_BATCH_META_SUCCESS) still depends on.
	const meta = {};
	if (captured.google_client_id) meta.gcid = captured.google_client_id;
	if (captured.gclid) meta.gclid = captured.gclid;

	return { entries, meta, gcidValue: captured.google_client_id };
}

// In-memory flags to prevent multiple concurrent runs of each capture path.
// Kept separate (rather than one shared flag) so recheckClickIds - which can
// fire before getUserGAClientId on the very first page load, since child
// component effects flush before the ApplicationWrapper effect that triggers
// APP.READY - can never block the authoritative retry loop from starting.
// Duplicate posts across the two paths are prevented separately via
// pendingKeys above.
let initialCaptureInProgress = false;
let recheckInProgress = false;

/**
 * Capture and save tracking meta (gcid and gclid) with unified batching
 * Strategy:
 * - If gcid IS available: send immediately (batched with gclid if needed)
 * - If gcid is NOT available: retry until found or max retries, then send what we have
 * - sessionStorage tracks which keys have been sent for this visitId across page reloads
 * - In-memory flag prevents multiple concurrent retry loops
 */
export const getUserGAClientId = () => (dispatch, getState) => {
	// Prevent multiple concurrent retry loops in the same page load
	if (initialCaptureInProgress) {
		return Promise.resolve();
	}

	let retries = 0;

	function attemptCapture() {
		const { user } = getState();
		const { entries, meta, gcidValue } = buildMetaEntries(user);

		// If gcid is available, we can make a decision
		if (gcidValue) {
			// Nothing new to send - all keys already sent for this visitId
			if (entries.length === 0) {
				initialCaptureInProgress = false;
				return Promise.resolve();
			}

			initialCaptureInProgress = true;
			markKeysPending(entries);
			return dispatch(saveBatchMeta(entries, meta))
				.then((result) => {
					if (result?.type === USER.SAVE_BATCH_META_SUCCESS) {
						markEntriesAsSent(user.visitId, entries);
					} else {
						logBatchMetaFailure(result);
					}
				})
				.finally(() => {
					initialCaptureInProgress = false;
					unmarkKeysPending(entries);
				});
		}

		// gcid not available yet - retry until we get it or exhaust retries
		if (retries < MAX_RETRIES) {
			retries += 1;
			initialCaptureInProgress = true;
			return new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS)).then(attemptCapture);
		}

		// Max retries reached - send whatever we have (likely just gclid if any)
		if (entries.length > 0) {
			initialCaptureInProgress = true;
			markKeysPending(entries);
			return dispatch(saveBatchMeta(entries, meta))
				.then((result) => {
					if (result?.type === USER.SAVE_BATCH_META_SUCCESS) {
						markEntriesAsSent(user.visitId, entries);
					} else {
						logBatchMetaFailure(result);
					}
				})
				.finally(() => {
					initialCaptureInProgress = false;
					unmarkKeysPending(entries);
				});
		}

		initialCaptureInProgress = false;
		return Promise.resolve();
	}

	return attemptCapture();
};

/**
 * Opportunistic single-shot recheck, meant to be called on every page view
 * (not just initial app load). GA can finish loading well after the initial
 * 3s retry window in getUserGAClientId gives up; this lets a later page view
 * pick up anything that arrived late (most commonly google_client_id)
 * without re-running the full retry loop. No-ops immediately if nothing is
 * missing - buildMetaEntries only returns keys not already in alreadySent.
 * Defers to an in-progress initial capture rather than racing it, so it can
 * never starve getUserGAClientId's retry loop of its chance to run.
 */
export const recheckClickIds = () => (dispatch, getState) => {
	if (initialCaptureInProgress || recheckInProgress) {
		return Promise.resolve();
	}

	const { user } = getState();
	const { entries, meta } = buildMetaEntries(user);

	if (entries.length === 0) {
		return Promise.resolve();
	}

	recheckInProgress = true;
	markKeysPending(entries);
	return dispatch(saveBatchMeta(entries, meta))
		.then((result) => {
			if (result?.type === USER.SAVE_BATCH_META_SUCCESS) {
				markEntriesAsSent(user.visitId, entries);
			} else {
				logBatchMetaFailure(result);
			}
		})
		.finally(() => {
			recheckInProgress = false;
			unmarkKeysPending(entries);
		});
};

// For testing: reset all module-level and session state
export const resetTrackingMetaFlag = () => {
	initialCaptureInProgress = false;
	recheckInProgress = false;
	pendingKeys = new Set();
	if (typeof sessionStorage !== 'undefined') {
		try {
			sessionStorage.removeItem(STORAGE_KEY);
		} catch {
			// Ignore errors
		}
	}
};
